import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Github, Play, Calendar, TrendingUp, DollarSign } from 'lucide-react';
import { projectsData } from '../data/projects';
import { fadeInUp, staggerContainer } from '../utils/animations';

const ProjectDetails = () => {
  const { id } = useParams<{ id: string }>();
  const project = projectsData.find((p) => p.id === id);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-surface">
        <div className="text-center">
          <h1 className="text-h1 font-bold mb-4">Project Not Found</h1>
          <Link to="/projects" className="text-neon-purple hover:underline">
                Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen pt-32 pb-20 bg-dark-surface"
    >
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <Link
          to="/projects"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-neon-purple transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Projects
        </Link>
      </div>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="relative h-96 rounded-3xl overflow-hidden mb-8">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-dark-surface via-dark-surface/50 to-transparent" />
          
          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 glass-morphism rounded-full text-sm font-medium">
                  {project.category}
                </span>
                {project.featured && (
                  <span className="px-3 py-1 bg-gradient-to-r from-neon-purple to-neon-blue rounded-full text-sm font-bold">
                    Featured
                  </span>
                )}
              </div>
              <h1 className="text-h1 font-bold mb-4">{project.title}</h1>
              <p className="text-body-lg text-text-secondary max-w-3xl">
                {project.longDescription}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Metrics */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {project.metrics.users && (
            <motion.div variants={fadeInUp} className="glass-morphism p-6 rounded-2xl">
              <TrendingUp size={24} className="text-neon-purple mb-2" />
              <div className="text-h3 font-bold mb-1">{project.metrics.users}</div>
              <div className="text-text-secondary">Active Users</div>
            </motion.div>
          )}
          {project.metrics.performance && (
            <motion.div variants={fadeInUp} className="glass-morphism p-6 rounded-2xl">
              <Calendar size={24} className="text-neon-blue mb-2" />
              <div className="text-h3 font-bold mb-1">{project.metrics.performance}</div>
              <div className="text-text-secondary">Uptime</div>
            </motion.div>
          )}
          {project.metrics.revenue && (
            <motion.div variants={fadeInUp} className="glass-morphism p-6 rounded-2xl">
              <DollarSign size={24} className="text-neon-purple mb-2" />
              <div className="text-h3 font-bold mb-1">{project.metrics.revenue}</div>
              <div className="text-text-secondary">Revenue Generated</div>
            </motion.div>
          )}
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-morphism p-8 rounded-2xl"
            >
              <h2 className="text-h3 font-bold mb-4">About This Project</h2>
              <p className="text-text-secondary leading-relaxed">{project.longDescription}</p>
            </motion.div>

            {/* Timeline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-morphism p-8 rounded-2xl"
            >
              <h2 className="text-h3 font-bold mb-6">Development Timeline</h2>
              <div className="space-y-6">
                {project.timeline.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 rounded-full bg-gradient-to-br from-neon-purple to-neon-blue" />
                      {index < project.timeline.length - 1 && (
                        <div className="w-0.5 h-full bg-glass-border mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="text-sm text-neon-purple font-medium mb-1">
                        {item.date}
                      </div>
                      <div className="font-semibold mb-1">{item.title}</div>
                      <div className="text-sm text-text-secondary">{item.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tech Stack */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-morphism p-6 rounded-2xl"
            >
              <h3 className="text-h4 font-bold mb-4">Tech Stack</h3>
              <div className="flex flex-wrap gap-2">
                {project.techStack.map((tech, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-dark-surface-200 rounded-lg text-sm text-text-secondary"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Tags */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="glass-morphism p-6 rounded-2xl"
            >
              <h3 className="text-h4 font-bold mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {project.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gradient-to-r from-neon-purple/20 to-neon-blue/20 border border-neon-purple/30 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-morphism p-6 rounded-2xl space-y-3"
            >
              {project.demoUrl && (
                <a
                  href={project.demoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-gradient-to-r from-neon-purple to-neon-blue text-white font-semibold rounded-xl hover:shadow-glow-purple transition-all"
                >
                  <Play size={18} />
                  Live Demo
                </a>
              )}
              {project.githubUrl && (
                <a
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full px-6 py-3 glass-morphism text-text-primary font-semibold rounded-xl hover:border-neon-purple transition-all"
                >
                  <Github size={18} />
                  View Code
                </a>
              )}
              <a
                href="#contact"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 glass-morphism text-text-primary font-semibold rounded-xl hover:border-neon-purple transition-all"
              >
                <ExternalLink size={18} />
                Contact Us
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectDetails;
